<template>
  <ModalRoot v-model="isOpen" prevent-close-on-backdrop-click>
    <template #body>
      <ModalBodyV2
        class="!bg-fill-brand-secondary !backdrop-blur-[50px] !max-w-[497px] items-center"
        @close="handleClose"
      >
        <template #left>
          <div class="w-10 shrink-0" />
        </template>

        <template #title>
          <h2 class="font-bold text-3xl text-text leading-normal text-center">
            {{ t("emailConsent.title") }}
          </h2>
        </template>

        <!-- Header: icon + description -->
        <div
          class="flex flex-col items-center justify-center overflow-hidden w-full shrink-0"
        >
          <Icon
            icon="material-symbols:notifications-unread"
            class="size-[70px] text-fill-brand"
            aria-hidden="true"
          />
          <p
            class="font-medium text-lg text-text leading-7 text-center whitespace-pre-wrap px-1 py-4"
          >
            {{ contextDescription }}
          </p>
        </div>

        <!-- Action buttons -->
        <div class="flex flex-col gap-[10px] items-start w-full shrink-0">
          <!-- Enable Email Notifications - primary brand button -->
          <ButtonV2
            variant="primary"
            size="lg"
            :disabled="isLoading"
            :is-loading="isLoading"
            class="w-full"
            @click="handleOptIn"
          >
            <span>{{ t("emailConsent.enableNotifications") }}</span>
          </ButtonV2>

          <!-- Remind Me Later - secondary button -->
          <ButtonV2
            variant="subtle"
            size="lg"
            :disabled="isLoading"
            :is-loading="isLoading"
            class="w-full"
            @click="handleSnooze"
          >
            <span>{{ t("emailConsent.remindLater") }}</span>
          </ButtonV2>

          <!-- Don't Ask Again - secondary button -->
          <ButtonV2
            variant="subtle"
            size="lg"
            :disabled="isLoading"
            :is-loading="isLoading"
            class="w-full"
            @click="handleOptOut"
          >
            <span>{{ t("emailConsent.dontAskAgain") }}</span>
          </ButtonV2>
        </div>
      </ModalBodyV2>
    </template>
  </ModalRoot>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Icon } from "@iconify/vue";
import { useMutation } from "@urql/vue";
import { graphql as gql } from "@/generated";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import ModalBodyV2 from "@/components/ui/modal/modal-body-v2.vue";
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import { useJustOneOpenModal } from "@/composables/buses/use-modal-bus";
import { useUserStore } from "@/stores/user.store";

const props = withDefaults(
  defineProps<{
    context?: "show" | "thread" | "contest" | "channel" | "generic";
  }>(),
  {
    context: "generic",
  },
);

const emit = defineEmits<{
  decided: [optedIn: boolean];
  snoozed: [];
}>();

const isOpen = defineModel<boolean>({ required: true });

useJustOneOpenModal(isOpen);

const { t } = useI18n();
const userStore = useUserStore();
const isLoading = ref(false);

const SetEmailConsentDocument = gql(/* GraphQL */ `
  mutation SetEmailConsentV2($opt_in: Boolean!, $source: String) {
    setEmailConsent(opt_in: $opt_in, source: $source) {
      ... on Me {
        ...UserStoreMe
      }
      ... on UnauthenticatedError {
        error
      }
    }
  }
`);

const SnoozeEmailConsentDocument = gql(/* GraphQL */ `
  mutation SnoozeEmailConsentV2 {
    snoozeEmailConsent {
      ... on Me {
        ...UserStoreMe
      }
      ... on UnauthenticatedError {
        error
      }
    }
  }
`);

const { executeMutation: executeSetEmailConsent } = useMutation(
  SetEmailConsentDocument,
);

const { executeMutation: executeSnoozeEmailConsent } = useMutation(
  SnoozeEmailConsentDocument,
);

const contextDescription = computed(() => {
  switch (props.context) {
    case "show":
      return t("emailConsent.context.show");
    case "thread":
      return t("emailConsent.context.thread");
    case "contest":
      return t("emailConsent.context.contest");
    case "channel":
      return t("emailConsent.context.channel");
    case "generic":
    default:
      return t("emailConsent.context.generic");
  }
});

function handleClose() {
  isOpen.value = false;
}

async function handleOptIn() {
  isLoading.value = true;
  try {
    await executeSetEmailConsent({ opt_in: true, source: "consent_modal" });
    await userStore.refetchLocalUser();
    emit("decided", true);
    isOpen.value = false;
  } finally {
    isLoading.value = false;
  }
}

async function handleOptOut() {
  isLoading.value = true;
  try {
    await executeSetEmailConsent({ opt_in: false, source: "consent_modal" });
    await userStore.refetchLocalUser();
    emit("decided", false);
    isOpen.value = false;
  } finally {
    isLoading.value = false;
  }
}

async function handleSnooze() {
  isLoading.value = true;
  try {
    await executeSnoozeEmailConsent({});
    await userStore.refetchLocalUser();
    emit("snoozed");
    isOpen.value = false;
  } finally {
    isLoading.value = false;
  }
}
</script>
